import LegalLayout, { Section } from "../components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026">
      <p>
        Vixelforge ("we", "us") operates www.vixelforge.com and the Vixelforge web application (the "Service").
        This Privacy Policy explains what personal data we collect, why we collect it, and the rights you have over it.
        By using the Service you agree to this Policy.
      </p>

      <Section heading="Data we collect">
        <p>
          <strong>Account data:</strong> email address, name, and (for Google sign-in) your Google profile picture.
          If you use email + password, we store a one-way bcrypt hash of your password. We never see or store the plain text password.
        </p>
        <p>
          <strong>Usage data:</strong> the prompts you generate, the questionnaire answers you submit, and the AI model you selected.
          This is stored so we can render your history, favorites, and monthly quota. It is never used to train third-party AI models.
        </p>
        <p>
          <strong>Generated media:</strong> when you generate an image or video, the full-resolution file is saved directly to <em>your</em> chosen destination — your local folder or your Google Drive. On our servers we keep only a small thumbnail and lightweight metadata (prompt link, model, status) so you can browse your Gallery. We do not store your full-resolution media.
        </p>
        <p>
          <strong>Billing data:</strong> handled entirely by Stripe. We store only the Stripe customer ID and subscription ID.
          We do not store or ever see your card number, CVC, or full billing details.
        </p>
        <p>
          <strong>Technical data:</strong> IP address, browser type, and a minimum set of application logs for troubleshooting and abuse prevention.
        </p>
      </Section>

      <Section heading="Third parties we use">
        <p>
          <strong>Anthropic (Claude Sonnet 4.5)</strong> — receives your questionnaire input to generate the optimized prompt. Anthropic does not use API data to train its models.
        </p>
        <p>
          <strong>fal.ai</strong> — generates the images and videos you request. Your prompt text (and any reference image you choose to attach) is sent to fal.ai to produce the media. fal.ai does not use this data to train models. The generated file is returned to your browser and saved to your chosen destination (local folder or Google Drive).
        </p>
        <p>
          <strong>Stripe</strong> — payment processing and EU VAT compliance.
        </p>
        <p>
          <strong>Google (Sign-in)</strong> — only if you choose "Continue with Google", used solely to authenticate you.
        </p>
        <p>
          <strong>Google Drive</strong> — only if you choose to connect it as a storage destination (see the dedicated section below).
        </p>
        <p>
          <strong>Resend</strong> — sends transactional emails (password reset, receipts).
        </p>
      </Section>

      <Section heading="Google Drive access">
        <p>
          Connecting Google Drive is <strong>optional</strong>. If you choose it as a save destination, you grant Vixelforge the
          single scope <code>https://www.googleapis.com/auth/drive.file</code>.
        </p>
        <p>
          This is a per-file scope: Vixelforge can only create and manage the files it saves on your behalf (inside a
          "Vixelforge" folder). <strong>It does not let us read, list, or access any other file in your Drive.</strong>
        </p>
        <p>
          How we use it: when you generate media and pick "Google Drive" as the destination, the file is uploaded from your
          session directly into your Drive. We store only an OAuth refresh token (to upload on your behalf), the ID of the
          Vixelforge folder, and a small thumbnail. We never read your existing Drive content, and we never store your
          full-resolution files on our servers.
        </p>
        <p>
          You can revoke access at any time from the app (Disconnect Drive) or from your
          <a className="text-orange-400 hover:text-orange-300" href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer"> Google Account permissions</a>.
        </p>
      </Section>

      <Section heading="Limited Use of Google user data">
        <p>
          Vixelforge's use and transfer of information received from Google APIs to any other app will adhere to the
          <a className="text-orange-400 hover:text-orange-300" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer"> Google API Services User Data Policy</a>,
          including the <strong>Limited Use</strong> requirements.
        </p>
        <p>
          Specifically: data obtained via Google Drive (the <code>drive.file</code> scope) is used only to provide the
          user-facing media-saving feature; it is not transferred or sold to third parties, not used for advertising, and
          not used to train generalized AI/ML models. Humans do not read this data except where you explicitly request
          support, for security/abuse investigations, or where required by law.
        </p>
      </Section>

      <Section heading="Your rights (GDPR)">
        <p>
          You have the right to access, correct, export, or delete your personal data at any time.
          Email <a className="text-orange-400 hover:text-orange-300" href="mailto:privacy@vixelforge.com">privacy@vixelforge.com</a> and we will respond within 30 days.
        </p>
        <p>
          You also have the right to lodge a complaint with your local data protection authority.
        </p>
      </Section>

      <Section heading="Retention">
        <p>
          Your prompts and media are retained while your subscription is active. When a subscription ends: images and videos stored on
          Vixelforge are permanently deleted after 30 days (with an email reminder 7 days before); prompts, lists, previews and reference
          images are kept for 12 months and then permanently deleted (with an email reminder 30 days before). Subscribing again before
          these dates cancels the deletion. Files saved to your Google Drive or local folder are never touched. You can delete your
          account at any time from the Account page: deletion is immediate and permanent and also cancels any active subscription.
          Images generated during the free trial (without a paid plan) are kept for 7 days and then permanently deleted, together
          with their previews (with an email reminder 2 days before); activating a plan before that date keeps them.
          We only keep records required for legal or accounting reasons (typically Stripe invoices, up to 10 years).
        </p>
      </Section>

      <Section heading="Contact">
        <p>
          Data controller: Vixelforge · <a className="text-orange-400 hover:text-orange-300" href="mailto:privacy@vixelforge.com">privacy@vixelforge.com</a>
        </p>
      </Section>
    </LegalLayout>
  );
}
